import { inject, Injectable, Type } from '@angular/core';
import { JsonApiLoaderService } from './json-api-loader.service';
import {
  combineLatest,
  delay,
  filter,
  map,
  Observable,
  of,
  pipe,
  shareReplay,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import { ComponentStore } from '@ngrx/component-store';
import { GetEntitiesPipe, GetRequestPipe } from './+state/get-data-item.pipe';
import { JsonApiQuery, ListJsonApiQuery } from './json-api.query';
import { isEqual } from '@madeinlune/ngx-operators';
import { FacadeService } from './facade.service';
import { DrupalIdentifier } from '@madeinlune/drupal-types';
import { GetUiEntityPipe } from './+state/get-ui-data-item.pipe';

export type LoadingState = 'idle' | 'initialLoading' | 'moreLoading';

export interface EntitiesState<T> {
  entities: T[];
  ids?: string[];
  loaded?: boolean;
  error?: Error;
  offset?: number | undefined;
  queryId?: string;
  loadingState: LoadingState;
  initialSuccess: boolean;
  errorMessage?: string;
}

const initialState: EntitiesState<any> = {
  loaded: false,
  entities: [],
  ids: [],
  queryId: undefined,
  error: undefined,
  loadingState: 'idle',
  initialSuccess: false,
};

@Injectable()
export class EntitiesStore<T extends DrupalIdentifier> extends ComponentStore<
  EntitiesState<T>
> {
  public modelClass: Type<any> | undefined;

  private getUiEntityPipe = inject(GetUiEntityPipe);

  readonly queryId$ = this.select((s) => s.queryId);

  readonly initialSuccess$ = this.select((s) => s.initialSuccess);

  readonly notInitialSuccess$ = this.select((s) => !s.initialSuccess);

  readonly entities$ = this.select((s) => s.entities);

  readonly hasEmptyResult$ = combineLatest([
    this.entities$,
    this.initialSuccess$,
  ]).pipe(
    map(([entities, initialSuccess]) => {
      return initialSuccess && entities.length === 0;
    })
  );

  readonly loading$ = this.select((s) => {
    return (
      s.loadingState === 'initialLoading' || s.loadingState === 'moreLoading'
    );
  });

  readonly offset$ = this.select((s) => s.offset);

  readonly error$ = this.select((s) => s.error);

  readonly errorMessage$ = this.select((s) => s.errorMessage);

  private destroyEntitiesMonitoring$: Subject<void> | undefined;

  readonly richError$ = combineLatest([this.errorMessage$, this.error$]).pipe(
    map(([errorMessage, error]) => {
      const message = errorMessage;
      if (message) {
        const errors = [];
        if (error) {
          errors.push(error);
        }
        return { message, errors };
      }
      return undefined;
    })
  );

  load = this.effect<{ query: ListJsonApiQuery; context?: string }>(
    pipe(
      tap(() => {
        this.patchState(initialState);
        this.cancelEntitiesMonitoring();
      }),
      delay(0),
      tap((queryInfos: { query: JsonApiQuery; context?: string }) => {
        const { query, context } = queryInfos;
        const queryId = context ? `${query.queryId}-${context}` : query.queryId;
        const clonedQuery: ListJsonApiQuery = {
          ...query,
          queryId,
        };
        this.patchState({ queryId, loadingState: 'initialLoading' });
        this.facade.loadList(clonedQuery);
        this.enableEntitiesMonitoring();
        if (this.destroyEntitiesMonitoring$) {
          this.patchState(
            this.getRequest.transform(queryId).pipe(
              takeUntil(this.destroyEntitiesMonitoring$),
              takeUntil(this.destroy$),
              isEqual(),
              switchMap((storeQuery: ListJsonApiQuery | undefined) => {
                const allResultIds = storeQuery?.allResultIds ?? [];
                if (this.destroyEntitiesMonitoring$) {
                  return this.getEntitiesPipe.transform<T>(allResultIds).pipe(
                    takeUntil(this.destroyEntitiesMonitoring$),
                    takeUntil(this.destroy$),
                    switchMap((entities) => {
                      if (entities.length > 0) {
                        return combineLatest(
                          entities.map((e) => {
                            return this.getUiEntityPipe.transform<T>(
                              e.id,
                              true,
                              this.modelClass
                            );
                          })
                        );
                      }
                      return of([]);
                    }),
                    takeUntil(this.destroyEntitiesMonitoring$),
                    takeUntil(this.destroy$),
                    isEqual(),
                    map((entities: T[]) => {
                      console.log({ storeQuery });
                      const loadingState = this.get((s) => s.loadingState);
                      //check if query is success, or if query has already loaded data, don't wait for remote response
                      const successCriteria =
                        storeQuery?.state === 'success' ||
                        allResultIds.length > 0;
                      const entitiesState: EntitiesState<any> = {
                        entities,
                        loadingState: successCriteria ? 'idle' : loadingState,
                        initialSuccess: successCriteria,
                      };
                      if (storeQuery?.state === 'error' && storeQuery.error) {
                        entitiesState.error = storeQuery.error;
                        entitiesState.errorMessage =
                          storeQuery.options.errorContent.message;
                        entitiesState.loadingState = 'idle';
                      } else if (
                        loadingState === 'initialLoading' &&
                        successCriteria
                      ) {
                        entitiesState.initialSuccess = true;
                      }
                      console.log({ entitiesState });
                      return entitiesState;
                    }),
                    shareReplay(1)
                  );
                }
                return of(initialState);
              })
            )
          );
        }
      })
    )
  );

  constructor(
    private jsonApiLoaderService: JsonApiLoaderService,
    private getEntitiesPipe: GetEntitiesPipe,
    private getRequest: GetRequestPipe,
    private facade: FacadeService
  ) {
    super(initialState);
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.cancelEntitiesMonitoring();
  }

  private enableEntitiesMonitoring() {
    this.destroyEntitiesMonitoring$ = new Subject<void>();
  }

  private cancelEntitiesMonitoring() {
    if (this.destroyEntitiesMonitoring$) {
      this.destroyEntitiesMonitoring$.next();
      this.destroyEntitiesMonitoring$.complete();
    }
  }

  loadNextPage = this.effect<void>((trigger$) => {
    return trigger$.pipe(
      filter(() => {
        const loadingState = this.get((s) => s.loadingState);
        console.log({ loadingState });
        return (
          loadingState !== 'initialLoading' && loadingState !== 'moreLoading'
        );
      }),
      switchMap(() => {
        const queryId = this.get((s) => s.queryId);
        return this.getRequest
          .transform(queryId)
          .pipe(take(1)) as unknown as Observable<ListJsonApiQuery | undefined>;
      }),
      tap((query: ListJsonApiQuery | undefined) => {
        console.log({ query });
        if (query && query.canLoadMore) {
          const clonedQuery: ListJsonApiQuery = {
            ...query,
            state: 'loading',
            page: {
              offset: query.page.offset + query.page.limit,
              limit: query.page.limit,
            },
          };
          this.patchState({ loadingState: 'moreLoading' });
          this.facade.loadList(clonedQuery);
        }
      })
    );
  });

  #reset() {
    this.patchState(initialState);
  }
}
